import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Link,
	createFileRoute,
	useNavigate,
} from "@tanstack/react-router";
import { ChevronLeft, Loader2, Plus, Smartphone, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";

import { withJosa } from "@/lib/utils";

import { useRequest } from "@/hooks/use-request";

import { requireAdmin } from "@/middleware/auth";
import { usePackQuery } from "@/queries/pack";

export const Route = createFileRoute("/admin/pack/$pack_id")({
	beforeLoad: async () => {
		await requireAdmin();
	},
	component: RouteComponent,
});

const metaSchema = z.object({
	name: z.string().min(1, "이름은 필수입니다."),
	description: z.string().min(1, "설명은 필수입니다."),
	source: z.string().min(1, "출처는 필수입니다."),
});
type MetaValues = z.infer<typeof metaSchema>;

const addSlideSchema = z.object({
	slide_id: z
		.string()
		.min(1, "slide_id는 필수입니다.")
		.regex(/^[a-z0-9_-]+$/i, "영문/숫자/_/- 만 사용 가능합니다."),
});
type AddSlideValues = z.infer<typeof addSlideSchema>;

function RouteComponent() {
	const { pack_id } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { data, isLoading, error } = useQuery(usePackQuery(pack_id));

	const invalidate = async () => {
		await Promise.all([
			queryClient.invalidateQueries({ queryKey: ["pack", pack_id] }),
			queryClient.invalidateQueries({ queryKey: ["packs"] }),
		]);
	};

	if (isLoading) {
		return (
			<div className="flex justify-center py-20">
				<Loader2 className="size-8 animate-spin text-gray-400" />
			</div>
		);
	}

	if (error || !data) {
		return (
			<div className="container mx-auto px-4 py-6">
				<p className="text-red-500">Pack을 불러올 수 없습니다.</p>
				<Button asChild variant="outline" className="mt-4">
					<Link to="/admin">관리자 페이지로</Link>
				</Button>
			</div>
		);
	}

	const { pack, slides } = data;

	return (
		<div className="container mx-auto px-4 py-6 max-w-4xl space-y-8">
			<div className="flex items-center gap-2">
				<Button variant="ghost" size="sm" asChild>
					<Link to="/admin">
						<ChevronLeft className="size-4" />
						목록으로
					</Link>
				</Button>
				<h1 className="text-2xl font-bold flex-1 truncate">{pack.name}</h1>
				<DeletePackButton
					pack_id={pack.id}
					onDeleted={() => {
						invalidate();
						navigate({ to: "/admin" });
					}}
				/>
			</div>
			<p className="font-mono text-xs text-gray-500">{pack.id}</p>

			<MetaSection pack={pack} onSaved={invalidate} />
			<Separator />
			<ImageSection
				pack_id={pack.id}
				label="썸네일"
				endpoint={`admin/pack/${pack.id}/thumbnail`}
				currentUrl={pack.thumbnail_url}
				onSaved={invalidate}
			/>
			<Separator />
			<ImageSection
				pack_id={pack.id}
				label="소스 로고"
				endpoint={`admin/pack/${pack.id}/source-logo`}
				currentUrl={pack.source_logo_url}
				onSaved={invalidate}
			/>
			<Separator />
			<SlidesSection
				pack_id={pack.id}
				slides={slides}
				onChanged={invalidate}
			/>
		</div>
	);
}

function MetaSection({
	pack,
	onSaved,
}: {
	pack: IPack;
	onSaved: () => Promise<void> | void;
}) {
	const { patch } = useRequest();
	const [submitting, setSubmitting] = useState(false);

	const form = useForm<MetaValues>({
		resolver: zodResolver(metaSchema),
		defaultValues: {
			name: pack.name,
			description: pack.description,
			source: pack.source,
		},
	});

	useEffect(() => {
		form.reset({
			name: pack.name,
			description: pack.description,
			source: pack.source,
		});
	}, [pack.name, pack.description, pack.source, form]);

	const onSubmit = async (values: MetaValues) => {
		setSubmitting(true);
		try {
			await patch<IResponse>(`admin/pack/${pack.id}`, values);
			toast.success("메타데이터를 수정했습니다.");
			await onSaved();
		} catch {
			// toast handled by useRequest
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<section>
			<h2 className="text-lg font-semibold mb-3">메타데이터</h2>
			<Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
					<FormField
						control={form.control}
						name="name"
						render={({ field }) => (
							<FormItem>
								<FormLabel>이름</FormLabel>
								<FormControl>
									<Input {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="description"
						render={({ field }) => (
							<FormItem>
								<FormLabel>설명</FormLabel>
								<FormControl>
									<Textarea rows={3} {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="source"
						render={({ field }) => (
							<FormItem>
								<FormLabel>출처</FormLabel>
								<FormControl>
									<Input {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<Button type="submit" disabled={submitting}>
						{submitting ? <Loader2 className="size-4 animate-spin" /> : "저장"}
					</Button>
				</form>
			</Form>
		</section>
	);
}

function ImageSection({
	pack_id,
	label,
	endpoint,
	currentUrl,
	onSaved,
}: {
	pack_id: string;
	label: string;
	endpoint: string;
	currentUrl: string;
	onSaved: () => Promise<void> | void;
}) {
	const { putFormData } = useRequest();
	const [file, setFile] = useState<File | null>(null);
	const [submitting, setSubmitting] = useState(false);

	const upload = async () => {
		if (!file) {
			toast.error("파일을 선택해주세요.");
			return;
		}
		setSubmitting(true);
		try {
			const fd = new FormData();
			fd.append("image", file);
			await putFormData<IResponse>(endpoint, fd);
			toast.success(`${withJosa(label, "을", "를")} 업로드했습니다.`);
			setFile(null);
			await onSaved();
		} catch {
			// handled
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<section>
			<h2 className="text-lg font-semibold mb-3">{label}</h2>
			<div className="flex items-start gap-4 flex-wrap">
				<div className="w-40 h-40 bg-gray-100 rounded border flex items-center justify-center overflow-hidden">
					{currentUrl ? (
						<img
							src={currentUrl}
							alt={label}
							className="max-w-full max-h-full object-contain"
						/>
					) : (
						<span className="text-gray-400 text-sm">없음</span>
					)}
				</div>
				<div className="flex-1 min-w-[200px] space-y-3">
					<Label htmlFor={`file-${pack_id}-${label}`}>새 이미지 선택</Label>
					<Input
						id={`file-${pack_id}-${label}`}
						type="file"
						accept=".jpg,.jpeg,.png,.webp"
						onChange={(e) => setFile(e.target.files?.[0] ?? null)}
					/>
					<Button onClick={upload} disabled={!file || submitting}>
						{submitting ? (
							<Loader2 className="size-4 animate-spin" />
						) : (
							"업로드"
						)}
					</Button>
				</div>
			</div>
		</section>
	);
}

function SlidesSection({
	pack_id,
	slides,
	onChanged,
}: {
	pack_id: string;
	slides: ISlide[];
	onChanged: () => Promise<void> | void;
}) {
	const { postFormData, del } = useRequest();
	const [addOpen, setAddOpen] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [file, setFile] = useState<File | null>(null);

	const nextSlideId = `s_${String(slides.length + 1).padStart(2, "0")}`;

	const form = useForm<AddSlideValues>({
		resolver: zodResolver(addSlideSchema),
		defaultValues: { slide_id: nextSlideId },
	});

	useEffect(() => {
		if (addOpen) {
			form.reset({ slide_id: nextSlideId });
		}
	}, [addOpen, nextSlideId, form]);

	const handleAdd = async (values: AddSlideValues) => {
		if (!file) {
			toast.error("이미지를 선택해주세요.");
			return;
		}
		setSubmitting(true);
		try {
			const fd = new FormData();
			fd.append("slide_id", values.slide_id);
			fd.append("image", file);
			await postFormData<IResponse>(`admin/pack/${pack_id}/slide`, fd);
			toast.success("슬라이드를 추가했습니다.");
			form.reset({ slide_id: "" });
			setFile(null);
			setAddOpen(false);
			await onChanged();
		} catch {
			// handled
		} finally {
			setSubmitting(false);
		}
	};

	const handleDelete = async (slide_id: string) => {
		if (!confirm(`슬라이드 "${slide_id}"를 삭제할까요?`)) return;
		try {
			await del<IResponse>(`admin/pack/${pack_id}/slide/${slide_id}`);
			toast.success("슬라이드를 삭제했습니다.");
			await onChanged();
		} catch {
			// handled
		}
	};

	return (
		<section>
			<div className="flex items-center justify-between mb-3">
				<h2 className="text-lg font-semibold">슬라이드 ({slides.length}개)</h2>
				<Dialog open={addOpen} onOpenChange={setAddOpen}>
					<DialogTrigger asChild>
						<Button size="sm">
							<Plus className="size-4" />
							슬라이드 추가
						</Button>
					</DialogTrigger>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>슬라이드 추가</DialogTitle>
						</DialogHeader>
						<Form {...form}>
							<form
								onSubmit={form.handleSubmit(handleAdd)}
								className="space-y-4"
							>
								<FormField
									control={form.control}
									name="slide_id"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Slide ID</FormLabel>
											<FormControl>
												<Input placeholder="예: s_01" {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<div className="space-y-2">
									<Label htmlFor="add-slide-file">이미지</Label>
									<Input
										id="add-slide-file"
										type="file"
										accept=".jpg,.jpeg,.png,.webp"
										onChange={(e) => setFile(e.target.files?.[0] ?? null)}
									/>
								</div>
								<DialogFooter>
									<Button
										type="button"
										variant="ghost"
										onClick={() => setAddOpen(false)}
									>
										취소
									</Button>
									<Button type="submit" disabled={submitting}>
										{submitting ? (
											<Loader2 className="size-4 animate-spin" />
										) : (
											"추가"
										)}
									</Button>
								</DialogFooter>
							</form>
						</Form>
					</DialogContent>
				</Dialog>
			</div>

			{slides.length === 0 ? (
				<p className="text-gray-500 text-sm py-6 text-center">
					아직 슬라이드가 없습니다.
				</p>
			) : (
				<ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
					{slides.map((slide) => (
						<li
							key={slide.id}
							className="border rounded-lg overflow-hidden flex flex-col"
						>
							<div className="aspect-video bg-gray-100 flex items-center justify-center">
								{slide.data ? (
									<img
										src={slide.data}
										alt={slide.id}
										className="w-full h-full object-cover"
									/>
								) : (
									<span className="text-gray-400 text-sm">이미지 없음</span>
								)}
							</div>
							<div className="p-3 flex-1 flex flex-col gap-2">
								<div>
									<div className="font-mono text-xs text-gray-500">
										{slide.id}
									</div>
									<div className="text-sm">
										seq {slide.seq} · 모바일 {slide.mobile.length}개
									</div>
								</div>
								<div className="flex gap-2 mt-auto">
									<MobileSlideDialog
										pack_id={pack_id}
										slide={slide}
										onChanged={onChanged}
									/>
									<Button
										variant="outline"
										size="sm"
										onClick={() => handleDelete(slide.id)}
									>
										<Trash2 className="size-4" />
									</Button>
								</div>
							</div>
						</li>
					))}
				</ul>
			)}
		</section>
	);
}

function MobileSlideDialog({
	pack_id,
	slide,
	onChanged,
}: {
	pack_id: string;
	slide: ISlide;
	onChanged: () => Promise<void> | void;
}) {
	const { postFormData, del } = useRequest();
	const [open, setOpen] = useState(false);
	const [file, setFile] = useState<File | null>(null);
	const [submitting, setSubmitting] = useState(false);

	const handleAdd = async () => {
		if (!file) {
			toast.error("이미지를 선택해주세요.");
			return;
		}
		setSubmitting(true);
		try {
			const fd = new FormData();
			fd.append("image", file);
			await postFormData<IResponse>(
				`admin/pack/${pack_id}/slide/${slide.id}/mobile`,
				fd,
			);
			toast.success("모바일 변형을 추가했습니다.");
			setFile(null);
			await onChanged();
		} catch {
			// handled
		} finally {
			setSubmitting(false);
		}
	};

	const handleDelete = async (seq: number) => {
		if (!confirm(`모바일 변형 #${seq}를 삭제할까요?`)) return;
		try {
			await del<IResponse>(
				`admin/pack/${pack_id}/slide/${slide.id}/mobile/${seq}`,
			);
			toast.success("삭제했습니다.");
			await onChanged();
		} catch {
			// handled
		}
	};

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button variant="outline" size="sm" className="flex-1">
					<Smartphone className="size-4" />
					모바일
				</Button>
			</DialogTrigger>
			<DialogContent className="max-w-xl">
				<DialogHeader>
					<DialogTitle>모바일 변형 — {slide.id}</DialogTitle>
				</DialogHeader>
				<div className="space-y-4">
					{slide.mobile.length === 0 ? (
						<p className="text-sm text-gray-500">변형이 없습니다.</p>
					) : (
						<ul className="space-y-2">
							{slide.mobile.map((m) => (
								<li
									key={m.seq}
									className="flex items-center gap-3 border rounded p-2"
								>
									<div className="w-16 h-16 bg-gray-100 rounded overflow-hidden flex items-center justify-center shrink-0">
										{m.data ? (
											<img
												src={m.data}
												alt={`mobile ${m.seq}`}
												className="w-full h-full object-cover"
											/>
										) : (
											<span className="text-xs text-gray-400">없음</span>
										)}
									</div>
									<div className="flex-1 text-sm">seq {m.seq}</div>
									<Button
										variant="outline"
										size="sm"
										onClick={() => handleDelete(m.seq)}
									>
										<Trash2 className="size-4" />
									</Button>
								</li>
							))}
						</ul>
					)}
					<div className="border-t pt-3 space-y-2">
						<Label>새 모바일 변형 추가</Label>
						<Input
							type="file"
							accept=".jpg,.jpeg,.png,.webp"
							onChange={(e) => setFile(e.target.files?.[0] ?? null)}
						/>
						<Button
							onClick={handleAdd}
							disabled={!file || submitting}
							size="sm"
						>
							{submitting ? (
								<Loader2 className="size-4 animate-spin" />
							) : (
								"추가"
							)}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}

function DeletePackButton({
	pack_id,
	onDeleted,
}: {
	pack_id: string;
	onDeleted: () => void;
}) {
	const { del } = useRequest();
	const [submitting, setSubmitting] = useState(false);

	const handleDelete = async () => {
		if (
			!confirm(
				`Pack "${pack_id}"를 정말 삭제할까요? 모든 슬라이드와 이미지가 함께 삭제됩니다.`,
			)
		) {
			return;
		}
		setSubmitting(true);
		try {
			await del<IResponse>(`admin/pack/${pack_id}`);
			toast.success("Pack을 삭제했습니다.");
			onDeleted();
		} catch {
			// handled
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Button
			variant="outline"
			size="sm"
			onClick={handleDelete}
			disabled={submitting}
			className="text-red-600 hover:text-red-700"
		>
			{submitting ? (
				<Loader2 className="size-4 animate-spin" />
			) : (
				<Trash2 className="size-4" />
			)}
			Pack 삭제
		</Button>
	);
}
